import { ChangeDetectorRef, Directive, EmbeddedViewRef, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject, of } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";

import { BannerDto, BannerService } from "@app/user/user-core/services/banner.service";
import { UserProfile } from "@helper/abstraction/user";

export interface BannerStore {
  click: (bannerId: string) => void;
  close: (bannerId: string) => void;
}

interface BannerTemplateContext {
  $implicit: BannerDto;
  store: BannerStore;
}

@Directive({
  selector: '[appBannerLoader]',
  providers: [BannerService]
})
export class BannerLoaderDirective implements OnDestroy, OnChanges {
  @Input('appBannerLoader') data!: { location: string, profile?: UserProfile };
  @Input() appBannerLoaderApp: string = 'edi';

  private readonly destroy$: Subject<void> = new Subject<void>();
  private viewRef: EmbeddedViewRef<BannerTemplateContext> | null = null;
  private currentBanner: BannerDto | null = null;
  private readonly ANIMATION_MS = 400;

  public readonly store: BannerStore = {
    click: (bannerId: string) => {
      this.api
        .click(bannerId)
        .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
        .subscribe();
    },

    close: (bannerId: string) => {
      const el = this.getRootElement();
      if (el) {
        el.classList.remove('banner-enter');
        el.classList.add('banner-leave');
      }
      this.currentBanner = null;
      setTimeout(() => {
        this.vcr.clear();
        this.viewRef = null;
      }, this.ANIMATION_MS);
      this.api
        .close(bannerId)
        .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
        .subscribe();
    },
  };

  constructor(
    private vcr: ViewContainerRef,
    private tpl: TemplateRef<BannerTemplateContext>,
    private api: BannerService,
    private cdr: ChangeDetectorRef,
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.data?.currentValue?.location !== changes?.data?.previousValue?.location) {
      this.loadBanner();
    }
    this.cdr.detectChanges();
  }

  private async loadBanner(): Promise<any> {
    await this.api
    .getBanner(this.appBannerLoaderApp, `${this.data.location?.toLocaleLowerCase()}Top`, this.data.profile!)
        .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
        .toPromise().then(banner => {
          this.currentBanner = banner;
          this.render();
        }).catch(() => {
          this.currentBanner = null;
          this.render();
        });
  }

  private render(): void {
    this.vcr.clear();
    this.viewRef = null;

    if (!this.currentBanner) return;

    this.viewRef = this.vcr.createEmbeddedView(this.tpl, {
      $implicit: this.currentBanner,
      store: this.store,
    });
    const el = this.getRootElement();
    if (el) {
      el.classList.remove('banner-leave');
      el.classList.add('banner-enter');
    }
  }

  private getRootElement(): HTMLElement | null {
    if (!this.viewRef) return null;
    const el = this.viewRef.rootNodes?.find((n) => n instanceof HTMLElement) as HTMLElement | undefined;
    return el ?? null;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.vcr.clear();
    this.viewRef = null;
  }
}
